import React, { useState, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper,
  Collapse,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAPI } from '../../../Hooks/useAPIs';
import { TimezoneContext } from '../../TimezoneContext/TimezoneContext';

const DeviceNotesDialog = ({ open, onClose, device }) => {
  const [noteText, setNoteText] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const { apiCall } = useAPI();
  const queryClient = useQueryClient();
  const { formatDateTime } = useContext(TimezoneContext);

  const notesQuery = useQuery({
    queryKey: ['device-notes', device?.id],
    queryFn: async () => {
      if (!device?.id) return [];
      const response = await apiCall('GET', `api/v1/devices/${device.id}/notes`);
      return response.data;
    },
    enabled: open && !!device?.id,
  });

  const addNoteMutation = useMutation({
    mutationFn: async (note) => {
      const response = await apiCall('POST', `api/v1/devices/${device.id}/notes`, {
        note,
        source: 'web-ui',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-notes', device?.id] });
      setNoteText('');
      setSubmitError('');
      setShowAddNote(false);
    },
    onError: (error) => {
      setSubmitError(error.response?.data?.detail || error.message || 'Failed to add note');
    },
  });

  const handleSubmit = () => {
    const trimmed = noteText.trim();
    if (!trimmed) return;
    setSubmitError('');
    addNoteMutation.mutate(trimmed);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  const handleClose = () => {
    setNoteText('');
    setShowAddNote(false);
    setSubmitError('');
    onClose();
  };

  const notes = Array.isArray(notesQuery.data) ? notesQuery.data : (notesQuery.data?.results || []);

  if (!device) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh', height: '80vh' },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Device Notes
          </Typography>
          <Typography variant="body2" color="text.secondary" fontFamily="monospace">
            {device.mac_addr}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Add Note Section */}
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Button
            variant={showAddNote ? 'outlined' : 'contained'}
            size="small"
            startIcon={showAddNote ? <ExpandLessIcon /> : <NoteAddIcon />}
            onClick={() => setShowAddNote(!showAddNote)}
            fullWidth
          >
            {showAddNote ? 'Cancel' : 'Add Note'}
          </Button>

          <Collapse in={showAddNote}>
            <Box sx={{ mt: 1.5 }}>
              <TextField
                multiline
                minRows={2}
                maxRows={5}
                fullWidth
                placeholder="Type your note here... (Ctrl+Enter to submit)"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                disabled={addNoteMutation.isLoading}
                autoFocus
              />
              {submitError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setSubmitError('')}>
                  {submitError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setShowAddNote(false);
                    setNoteText('');
                    setSubmitError('');
                  }}
                  disabled={addNoteMutation.isLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  endIcon={addNoteMutation.isLoading ? <CircularProgress size={16} /> : <SendIcon />}
                  onClick={handleSubmit}
                  disabled={!noteText.trim() || addNoteMutation.isLoading}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ mt: 1 }} />

        {/* Notes Timeline */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
          {notesQuery.isFetching && notes.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : notes.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <NoteAddIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No notes yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Add the first note for this device.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
              {notes.map((note, index) => (
                <Paper
                  key={note.id || index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderLeft: '3px solid',
                    borderLeftColor: note.source === 'web-ui' ? 'primary.main' : 'grey.400',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}
                  >
                    {note.note}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      {note.created_at ? formatDateTime(note.created_at, 'YYYY-MM-DD HH:mm:ss') : 'Unknown date'}
                    </Typography>
                    {note.source && (
                      <Chip label={note.source} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                    {note.user_detail && (
                      <Typography variant="caption" color="text.secondary">
                        by {note.user_detail.display_name || note.user_detail.email}
                      </Typography>
                    )}
                    {note.user_name && !note.user_detail && (
                      <Typography variant="caption" color="text.secondary">
                        by {note.user_name}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          {notes.length} note{notes.length !== 1 ? 's' : ''}
        </Typography>
        <Button onClick={handleClose} variant="contained" size="medium">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceNotesDialog;
